import { action, makeObservable, observable, runInAction } from 'mobx';
import { notifyError, notifySuccess } from '../../services/notifications';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { WindowState } from '../overlays/windowState';
import { input, InputState } from '..';
import isEmail from 'validator/lib/isEmail';
import { ExportClipsInput } from '@clipr/lib';

export class ExportToEdlWindowState
  extends StoreNode {
  readonly nodeType = 'ExportToEdlWindow';

  constructor(store: Store) {
    super(store);
    makeObservable(this);

    this.window.listen(
      this.windowListener);

    this.emailInput = input(this, {
      name: 'email',
      isRequired: false,
      placeholder: 'Email Address',
      error: input => {
        if (input.isRequired && input.isEmpty)
          return 'Required field';

        if (!input.isEmpty && !isEmail(input.value!.trim()))
          return 'Invalid email address format';

        return null;
      }
    });
  }

  readonly window = new WindowState(this.store);
  readonly emailInput: InputState;

  @observable isLoading: boolean = false;
  bookmarkListId: string | null = null;

  @action
  async submit() {
    if (!this.bookmarkListId) {
      notifyError(this, 'Could not download the list.');
      return this.handleSubmitFinally();
    }

    this.isLoading = true;

    const input: ExportClipsInput = {
      bookmarkListId: this.bookmarkListId,
      notificationEmail: this.emailInput.value ?? undefined
    }

    const [, err] = await this.store.apiService.exportBookmarkListToEdl({ args: input });

    if (err) {
      notifyError(this, 'Could not export the Bookmark list');
      return this.handleSubmitFinally();
    }

    runInAction(() => {
      notifySuccess(this, 'Email sent successfully.');
      this.handleSubmitFinally();
    });
  }

  handleSubmitFinally() {
    runInAction(() => {
      this.isLoading = false;
      this.close();
    });
  }

  @action
  private windowListener = (msg: Message<WindowState>) => {
    switch (msg.type) {
      case 'close':
      case 'outsideClick':
        this.close();
        break;
    }
  }

  @action
  open({ bookmarkListId }: { bookmarkListId: string }) {
    this.dispatch('Overlays', 'openWindow', { name: 'ExportToEdlWindow' });

    this.bookmarkListId = bookmarkListId;
  }

  @action
  close() {
    this.bookmarkListId = null;
    this.emailInput.clear();

    this.emit('close');
    this.dispatch('Overlays', 'closeWindow');
  }
}